import {
  Box,
  GDSSpace,
  GDSStyleObject,
  HeadingText,
  ParagraphText,
  StackLayout,
  useTextDirectionality,
} from '@leagueplatform/genesis-core';
import * as React from 'react';
import { easing, tween } from 'popmotion';
import {
  SWIPE_DIRECTION,
  SWIPE_ORIENTATION,
  useSwipeGestures,
} from '@leagueplatform/web-common';
import { uniqueId } from 'lodash-es';
import { LinkCTAProps } from '#types/carousel-common.types.ts';
import {
  NoPeekCarouselBackNavButton,
  NoPeekCarouselForwardNavButton,
} from './no-peek-carousel-nav-button.component';
import { ProgressDots } from './no-peek-carousel-progress-dots.component';
import { CTALink } from '../carousel/carousel-cta-link.component';
import {
  getPositionRelativeToParent,
  resizeObserverDebounce,
  slicePx,
  useGenesisSpaceTokenInPx,
} from './util/no-peek-carousel.utils';

enum NAV_BUTTON {
  forward = 'FORWARD',
  back = 'BACK',
}

const COMPLEX_WIDGET_CARDS_WIDTH = '275px';
const COMPLEX_WIDGET_CARDS_WIDTH_MOBILE = '200px';
const CAROUSEL_SCROLL_TIME = 505;

interface NoPeekCarouselGridWrapperProper {
  itemsPerSlide: number;
  as?: React.ElementType;
  gridColumnCount: number;
  gridChildMinWidth: number;
  gridGap: number;
  carouselWrapperRef: React.RefObject<HTMLUListElement>;
  children: React.ReactNode;
}

const NoPeekCarouselGridWrapper = ({
  as = 'ul',
  itemsPerSlide,
  gridChildMinWidth,
  gridColumnCount,
  gridGap: gap,
  carouselWrapperRef,
  children,
}: NoPeekCarouselGridWrapperProper) => {
  const gridWrapperChildrenCount = React.Children.count(children);
  const gapCount = itemsPerSlide - 1;
  const totalGapWidth = `${gapCount} * ${gap}px`;
  const maxGridItemWidth = `calc((100% - ${totalGapWidth}) / ${itemsPerSlide})`;

  // How many grid columns should we show
  // When the number of child items in the carousel is greater than the provided column count, we should create a template to fit the number of child items
  // Else create a template only for the number of items that are provided
  const gridTemplateColumnCount =
    gridWrapperChildrenCount > gridColumnCount
      ? gridWrapperChildrenCount
      : itemsPerSlide;

  const gridColumnStyles =
    itemsPerSlide === 1
      ? {
          gridAutoColumns: `minmax(max(${COMPLEX_WIDGET_CARDS_WIDTH_MOBILE}, calc(100% - var(--GDS-space-oneAndHalf))), 1fr)`,
        }
      : {
          gridTemplateColumns: `repeat(${gridTemplateColumnCount}, minmax(max(${gridChildMinWidth}px, ${maxGridItemWidth}), 1fr))`,
        };

  return (
    <Box
      as={as}
      css={{
        display: 'grid',
        ...gridColumnStyles,
        gridAutoFlow: 'column',
        height: '100%',
        gap,
        overflow: 'hidden',
        width: '100%',
        '@mobile': {
          gap: '$threeQuarters',
        },
      }}
      ref={carouselWrapperRef}
      aria-live="polite"
    >
      {children}
    </Box>
  );
};

export interface NoPeekCarouselProps {
  name: string;
  children?: React.ReactNode;
  spaceBetweenCards?: GDSSpace;
  heading?: React.ReactNode;
  description?: React.ReactNode;
  showProgressDots?: boolean;
  BackNavButtonAnalyticsFn?: () => void;
  ForwardNavButtonAnalyticsFn?: () => void;
  gridColumnCount?: number;
  itemMinWidth?: number;
  headingLabelledBy?: string;
  className?: string;
  listStyles?: GDSStyleObject;
}

export const NoPeekCarousel = React.forwardRef<
  HTMLDivElement,
  NoPeekCarouselProps & LinkCTAProps
>(
  (
    {
      children,
      gridColumnCount = 3,
      itemMinWidth = slicePx(COMPLEX_WIDGET_CARDS_WIDTH),
      name,
      heading,
      description,
      linkOnClick,
      linkAriaLabel,
      headingLabelledBy,
      spaceBetweenCards = '$one',
      linkCta,
      linkUrl,
      BackNavButtonAnalyticsFn = () => null,
      ForwardNavButtonAnalyticsFn = () => null,
      showProgressDots = false,
      listStyles = {},
      className,
    },
    ref,
  ) => {
    const dir = useTextDirectionality();

    const [itemsPerSlide, setItemsPerSlide] =
      React.useState<number>(gridColumnCount);
    const [forwardArrowDisabled, setForwardArrowDisabled] =
      React.useState(true);
    const [backArrowDisabled, setBackArrowDisabled] = React.useState(true);
    const [currentCarouselSlideIndex, setCurrentCarouselSlideIndex] =
      React.useState(0);

    // Refs for the carousel container and the carousel items container
    const carouselWrapperContainerRef = React.useRef<HTMLDivElement>(null);

    React.useImperativeHandle(
      ref,
      () => carouselWrapperContainerRef.current as HTMLDivElement,
      [],
    );

    const carouselItemsContainerRef = React.useRef<HTMLUListElement>(null);

    const swipeDirection = useSwipeGestures(
      carouselItemsContainerRef,
      SWIPE_ORIENTATION.horizontal,
    );

    const spaceBetweenCardsInPx = useGenesisSpaceTokenInPx(spaceBetweenCards);

    const childrenItems = React.Children.toArray(children);

    // Calculate how many slides we have in the carousel
    const numOfSlidesInCarousel = Math.ceil(
      childrenItems.length / itemsPerSlide,
    );

    const shouldShowProgressDots =
      showProgressDots && numOfSlidesInCarousel > 1;

    // Keeps track of the carousel items container and returns a computed object with the registry of items in the carousel and the current carousel items container ref
    const getCurrentCarouselItemsContainerProperties = React.useCallback(() => {
      const { current: currentCarouselItemsContainerRef } =
        carouselItemsContainerRef;

      if (currentCarouselItemsContainerRef) {
        const { childNodes } = currentCarouselItemsContainerRef;

        // Keep track of every item in the carousel
        const carouselItemsContainerRegistry: { [key: number]: HTMLElement } =
          {};

        childNodes.forEach((_, index) => {
          const carouselItem = childNodes[index] as HTMLElement;
          carouselItemsContainerRegistry[index] = carouselItem;
        });

        return {
          carouselItemsContainerRegistry,
          currentCarouselItemsContainerRef,
        };
      }

      return null;
    }, []);

    const setCarouselItemVisibility = React.useCallback(() => {
      const currentCarouselItemsContainerProperties =
        getCurrentCarouselItemsContainerProperties();

      if (currentCarouselItemsContainerProperties) {
        const { carouselItemsContainerRegistry } =
          currentCarouselItemsContainerProperties;

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.removeAttribute('aria-hidden');
                entry.target.removeAttribute('inert');
              } else {
                entry.target.setAttribute('aria-hidden', 'true');
                entry.target.setAttribute('inert', '');
              }
            });
          },
          {
            root: carouselItemsContainerRef.current,
            threshold: 0.2, // Trigger even if only 1 pixel is visible.  Adjust as needed.  1 means fully visible.
          },
        );

        Object.keys(carouselItemsContainerRegistry).forEach((key) => {
          const carouselItem = carouselItemsContainerRegistry[Number(key)];

          observer.observe(carouselItem);
        });
      }

      return null;
    }, [getCurrentCarouselItemsContainerProperties]);

    const setCarouselNavigationButtonsState = React.useCallback(
      (slideIndex: number, slideCount: number) => {
        // Set the initial state of the navigation buttons
        setForwardArrowDisabled(slideIndex === slideCount - 1);
        setBackArrowDisabled(slideIndex === 0);

        if (slideIndex > slideCount - 1) {
          setCurrentCarouselSlideIndex(slideCount - 1);
        }
      },
      [],
    );

    const scrollTo = React.useCallback(
      (amt = 0, duration = 0, onScrollComplete = () => {}) => {
        const { current: currentCarouselRef } = carouselItemsContainerRef;

        const to = dir === 'ltr' ? amt : -amt;

        if (currentCarouselRef) {
          const from = currentCarouselRef.scrollLeft;
          const scroll = tween({
            from,
            to,
            duration,
            ease: easing.easeOut,
          });
          scroll.start({
            update: (left: number) => {
              currentCarouselRef.scrollLeft = left;
            },
            complete: () => {
              setCarouselItemVisibility();
              onScrollComplete();
            },
          });
        }
      },
      [dir, setCarouselItemVisibility],
    );

    const onResizeCarouselContainerEventHandler = React.useCallback(() => {
      const currentCarouselItemsContainerProperties =
        getCurrentCarouselItemsContainerProperties();
      if (currentCarouselItemsContainerProperties) {
        const {
          carouselItemsContainerRegistry,
          currentCarouselItemsContainerRef: { offsetWidth, scrollLeft },
        } = currentCarouselItemsContainerProperties;

        // Calculate the minimum number of items that can fit in the carousel container after resizing
        const minItemsPerSlide = Math.floor(
          (offsetWidth + spaceBetweenCardsInPx) /
            (itemMinWidth + spaceBetweenCardsInPx),
        );

        // Clamp the calculated minimum number of items per slide to the max number of grid columns allowed
        const updatedItemsPerSlide = Math.max(
          1,
          Math.min(minItemsPerSlide, gridColumnCount),
        );

        // If the number of items per slide has changed, update the state
        if (updatedItemsPerSlide !== itemsPerSlide) {
          setItemsPerSlide(updatedItemsPerSlide);
        }

        // Check if the current slide index exists, if not reset to 0
        const doesCurrentSlideIndexExist =
          currentCarouselSlideIndex < numOfSlidesInCarousel;
        const updatedCarouselSlideIndex = doesCurrentSlideIndexExist
          ? currentCarouselSlideIndex
          : 0;

        if (updatedCarouselSlideIndex !== currentCarouselSlideIndex) {
          setCurrentCarouselSlideIndex(updatedCarouselSlideIndex);

          setCarouselNavigationButtonsState(
            updatedCarouselSlideIndex,
            numOfSlidesInCarousel,
          );
        } else {
          setCarouselNavigationButtonsState(
            currentCarouselSlideIndex,
            numOfSlidesInCarousel,
          );
        }

        // When resizing the carousel container, we need to ensure that the grid layout and no-peek functionality is maintained
        // We do this by scrolling back to the first item in the current slide
        const updatedCardIndex =
          updatedItemsPerSlide * updatedCarouselSlideIndex;
        const updatedScrollPosition = getPositionRelativeToParent(
          carouselItemsContainerRegistry[updatedCardIndex],
        ).left;

        // if the updated left position for the leftmost item is not at 0 - it is not touching its parents left border
        // trigger a scroll to the leftmost item
        if (updatedScrollPosition !== 0) {
          scrollTo(updatedScrollPosition + scrollLeft, CAROUSEL_SCROLL_TIME);
        }

        setCarouselItemVisibility();
      }
    }, [
      getCurrentCarouselItemsContainerProperties,
      spaceBetweenCardsInPx,
      itemMinWidth,
      gridColumnCount,
      itemsPerSlide,
      currentCarouselSlideIndex,
      numOfSlidesInCarousel,
      scrollTo,
      setCarouselNavigationButtonsState,
      setCarouselItemVisibility,
    ]);

    // Resize observer that keeps track of the carousel container and updates the number of items per slide when the container is resized
    // This is to ensure that the grid layout and no-peek functionality is maintained on resize
    React.useEffect(() => {
      const { current } = carouselWrapperContainerRef;

      if (current) {
        // Debounce the resize observer callback to prevent multiple calls to the callback
        const debounceCallback = resizeObserverDebounce(
          onResizeCarouselContainerEventHandler,
          250,
        );

        const carouselResizeObserver = new ResizeObserver(() => {
          debounceCallback();
        });

        carouselResizeObserver.observe(current);
        return () => {
          carouselResizeObserver.disconnect();
        };
      }
      onResizeCarouselContainerEventHandler();

      return () => null;
    }, [onResizeCarouselContainerEventHandler]);

    // Event handler for the navigation buttons
    const handleNavButtonEvent = React.useCallback(
      (type: NAV_BUTTON) => {
        const carouselItemsContainerProperties =
          getCurrentCarouselItemsContainerProperties();
        if (carouselItemsContainerProperties) {
          const {
            currentCarouselItemsContainerRef: { scrollLeft },
            carouselItemsContainerRegistry,
          } = carouselItemsContainerProperties;

          switch (type) {
            case NAV_BUTTON.forward:
              if (currentCarouselSlideIndex < numOfSlidesInCarousel - 1) {
                // Calculate the index of the card that will be visible after the user scrolls
                const nextCardIndex =
                  itemsPerSlide * (currentCarouselSlideIndex + 1);

                // Get the position of the card that will be visible after the user scrolls and add it to the user's current scroll position to get the new scroll position
                const scrollPosition =
                  getPositionRelativeToParent(
                    carouselItemsContainerRegistry[nextCardIndex],
                  ).left + scrollLeft;

                scrollTo(scrollPosition, CAROUSEL_SCROLL_TIME, () =>
                  setCurrentCarouselSlideIndex(currentCarouselSlideIndex + 1),
                );
              }

              break;
            case NAV_BUTTON.back:
              if (currentCarouselSlideIndex > 0) {
                // Calculate the index of the card that will be visible after the user scrolls
                const prevCardIndex =
                  itemsPerSlide * (currentCarouselSlideIndex - 1);

                // Get the position of the card that will be visible after the user scrolls and add it to the containers current scroll left position
                const scrollPosition =
                  getPositionRelativeToParent(
                    carouselItemsContainerRegistry[prevCardIndex],
                  ).left + scrollLeft;

                scrollTo(scrollPosition, CAROUSEL_SCROLL_TIME, () =>
                  setCurrentCarouselSlideIndex(currentCarouselSlideIndex - 1),
                );
              }
              break;

            default:
              break;
          }
        }
      },
      [
        getCurrentCarouselItemsContainerProperties,
        currentCarouselSlideIndex,
        numOfSlidesInCarousel,
        itemsPerSlide,
        scrollTo,
      ],
    );

    // Consolidate event handlers for the back navigation button
    const BackNavButtonEvent = React.useCallback(() => {
      handleNavButtonEvent(NAV_BUTTON.back);
      BackNavButtonAnalyticsFn();
    }, [BackNavButtonAnalyticsFn, handleNavButtonEvent]);

    // Consolidate event handlers for the forward navigation button
    const ForwardNavButtonEvent = React.useCallback(() => {
      handleNavButtonEvent(NAV_BUTTON.forward);
      ForwardNavButtonAnalyticsFn();
    }, [ForwardNavButtonAnalyticsFn, handleNavButtonEvent]);

    // Carousel is accessible on swipe for mobile devices/users with touch screen
    React.useEffect(() => {
      if (swipeDirection) {
        switch (swipeDirection) {
          case SWIPE_DIRECTION.right:
            if (dir === 'ltr') {
              ForwardNavButtonEvent();
            } else {
              BackNavButtonEvent();
            }
            break;
          case SWIPE_DIRECTION.left:
            if (dir === 'ltr') {
              BackNavButtonEvent();
            } else {
              ForwardNavButtonEvent();
            }
            break;
          default:
            break;
        }
      }
    }, [swipeDirection, dir, BackNavButtonEvent, ForwardNavButtonEvent]);

    // Always scroll to the start of the carousel on the components first mount
    React.useEffect(() => {
      scrollTo(0, 0);
    }, [scrollTo]);

    // Return null if no children are provided
    // Empty carousel state
    if (children === undefined) {
      // eslint-disable-next-line no-console
      console.warn(`No children found for carousel: ${name}`);
      return null;
    }

    // Warning if link CTA is provided without link URL or vice versa
    if (
      (!!linkUrl && linkCta === undefined) ||
      (!!linkCta && linkUrl === undefined)
    ) {
      if (linkCta === undefined) {
        // eslint-disable-next-line no-console
        console.warn(
          `Link CTA will not render. "linkUrl" prop provided, but "linkCta" prop missing for carousel: ${name}. Please provide both props.`,
        );
      } else {
        // eslint-disable-next-line no-console
        console.warn(
          `Link CTA will not render. "linkCta" prop provided, but "linkUrl" prop missing for carousel: ${name}. Please provide both props.`,
        );
      }
    }

    // Carousel Description can be a string or a React node
    // If string wrap in a paragraph text component
    // Else render the node
    let Description = description;
    if (description && typeof description === 'string') {
      Description = <ParagraphText>{description}</ParagraphText>;
    }

    const headingId = uniqueId('no-peek-carousel-');
    let Heading = heading;
    if (heading && typeof heading === 'string') {
      Heading = (
        <HeadingText level="2" size="xl" id={headingId}>
          {heading}
        </HeadingText>
      );
    }

    return (
      <Box
        data-testid="no-peek-carousel"
        aria-labelledby={headingLabelledBy ?? headingId}
        as="section"
        css={{
          width: '100%',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: '$one',
        }}
        ref={carouselWrapperContainerRef}
        className={className}
      >
        {/* Heading + Description */}
        <StackLayout spacing="$quarter">
          <StackLayout
            orientation="horizontal"
            horizontalAlignment={heading ? 'spaceBetween' : 'end'}
            css={{ width: '100%', alignItems: 'center' }}
          >
            {Heading}
            <Box
              css={{
                display: 'flex',
                marginLeft: '$threeQuarters',
                alignItems: 'center',
                gap: '$oneAndHalf',
                rowGap: '$threeQuarters',
              }}
            >
              {linkCta && linkUrl && (
                <CTALink
                  onClick={linkOnClick}
                  ariaLabel={linkAriaLabel}
                  href={linkUrl}
                >
                  {linkCta}
                </CTALink>
              )}
              {numOfSlidesInCarousel > 1 && (
                <Box
                  css={{
                    display: 'flex',
                    gap: '$one',
                    flexWrap: 'nowrap',
                    '@mobile': {
                      gap: '$quarter',
                    },
                  }}
                >
                  <NoPeekCarouselBackNavButton
                    disabled={backArrowDisabled}
                    onClick={BackNavButtonEvent}
                  />
                  <NoPeekCarouselForwardNavButton
                    disabled={forwardArrowDisabled}
                    onClick={ForwardNavButtonEvent}
                  />
                </Box>
              )}
            </Box>
          </StackLayout>
          <ParagraphText>{Description}</ParagraphText>
        </StackLayout>
        <Box
          css={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            overflowX: 'hidden',
            position: 'relative',
            gap: '$one',
          }}
        >
          <NoPeekCarouselGridWrapper
            carouselWrapperRef={carouselItemsContainerRef}
            itemsPerSlide={itemsPerSlide}
            gridChildMinWidth={itemMinWidth}
            gridColumnCount={gridColumnCount}
            gridGap={spaceBetweenCardsInPx}
          >
            {childrenItems.map((e, index) => (
              <Box
                as="li"
                css={{
                  minWidth: COMPLEX_WIDGET_CARDS_WIDTH,
                  '@mobile': {
                    minWidth: COMPLEX_WIDGET_CARDS_WIDTH_MOBILE,
                  },
                  ...listStyles,
                }}
                key={uniqueId('carousel-item-')}
                id={`carousel-item-${index}`}
              >
                {e}
              </Box>
            ))}
          </NoPeekCarouselGridWrapper>
        </Box>
        {shouldShowProgressDots && (
          <ProgressDots
            currentDot={currentCarouselSlideIndex}
            length={numOfSlidesInCarousel}
          />
        )}
      </Box>
    );
  },
);
